import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SearchBar from '../components/SearchBar';
import ResearchCard from '../components/ResearchCard';
import MarketAlertBanner from '../components/MarketAlertBanner';
import { startResearch, listResearch, refreshResearch, listDiscovered } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

const PAGE_SIZE = 12;
const DISC_PAGE = 6;

// System-discovered projects — full ResearchCard tiles, lazy "load more".
// Rendered as a left sidebar on desktop, below the list on mobile.
function DiscoveredPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadMore = useCallback(async (skip) => {
    setLoading(true);
    try {
      const { docs, total: t } = await listDiscovered({ limit: DISC_PAGE, skip });
      setTotal(t);
      setItems(prev => {
        const have = new Set(prev.map(d => d._id));
        return [...prev, ...docs.filter(d => !have.has(d._id))];
      });
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadMore(0); }, [loadMore]);

  if (items.length === 0) return null;

  return (
    <div style={ds.wrap}>
      <div style={ds.header}>
        <span style={ds.title}>🔎 {t('researchList.discoveredTitle')}</span>
        <span style={ds.sub}>{t('researchList.discoveredSub')}</span>
      </div>
      <div style={ds.list}>
        {items.map(d => (
          <ResearchCard key={d._id} doc={d} onClick={(slug) => navigate(`/research/${slug}`)} />
        ))}
      </div>
      {items.length < total && (
        <button style={ds.moreBtn} onClick={() => loadMore(items.length)} disabled={loading}>
          {loading ? t('researchList.loading') : t('researchList.loadMore', { count: total - items.length })}
        </button>
      )}
    </div>
  );
}

const ds = {
  wrap: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  header: { display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 10 },
  title: { fontSize: 13, fontWeight: 800, color: 'var(--text)' },
  sub: { fontSize: 11, color: 'var(--text-dim)' },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  moreBtn: {
    marginTop: 10, background: 'none', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text-muted)', fontSize: 11, fontWeight: 600, padding: '7px 14px', cursor: 'pointer',
    width: '100%',
  },
};

export default function ResearchListPage() {
  const { t } = useTranslation();
  const { marketAlerts, startPolling, loadList } = useOutletContext();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [ready, setReady]     = useState(false);   // first page loaded
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice]   = useState(null);
  const [error, setError]     = useState('');
  const sentinelRef = useRef(null);

  // Re-fetch the first page: refreshes statuses + surfaces newly started
  // research, merged into the already-loaded (paginated) list.
  const refreshFirst = useCallback(async () => {
    try {
      const { docs, total: t } = await listResearch({ limit: PAGE_SIZE, skip: 0 });
      setTotal(t);
      setItems(prev => {
        const ids = new Set(docs.map(d => d._id));
        const rest = prev.filter(d => !ids.has(d._id));
        return [...docs, ...rest];
      });
    } catch { /* silent */ }
  }, []);

  const loadMore = useCallback(async () => {
    setLoading(true);
    try {
      const { docs, total: t } = await listResearch({ limit: PAGE_SIZE, skip: items.length });
      setTotal(t);
      setItems(prev => {
        const have = new Set(prev.map(d => d._id));
        return [...prev, ...docs.filter(d => !have.has(d._id))];
      });
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [items.length]);

  // Initial load + poll the first page for freshness
  useEffect(() => {
    let active = true;
    (async () => { await refreshFirst(); if (active) setReady(true); })();
    const t = setInterval(refreshFirst, 8000);
    return () => { active = false; clearInterval(t); };
  }, [refreshFirst]);

  // Infinite scroll — load the next page when the sentinel comes into view
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      es => { if (es[0].isIntersecting && !loading && items.length < total) loadMore(); },
      { rootMargin: '300px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loading, items.length, total, loadMore]);

  const handleSearch = async (projectName) => {
    setError(''); setNotice(null); setSubmitting(true);
    try {
      const { source, research, message } = await startResearch(projectName);
      await refreshFirst();
      if (loadList) loadList(); // keep header counts fresh
      if (source === 'cache') {
        navigate(`/research/${research.slug}`, { state: { cacheMessage: message } });
      } else if (source === 'in_progress') {
        setNotice({ type: 'info', message: t('researchList.noticeInProgress') });
        startPolling(research.slug);
      } else {
        if (source === 'stale') setNotice({ type: 'stale', message });
        startPolling(research.slug);
      }
    } catch (e) {
      setError(e.premiumRequired ? t('researchList.errPremiumRequired') : e.message);
    }
    finally { setSubmitting(false); }
  };

  const handleReResearch = async (slug) => {
    setError('');
    try {
      await refreshResearch(slug);
      startPolling(slug);
      await refreshFirst();
      if (loadList) loadList();
    } catch (e) {
      setError(e.premiumRequired ? t('researchList.errReResearchPremium') : e.message);
    }
  };

  const runningJobs = items.filter(r => r.status === 'running' || r.status === 'pending');

  return (
    <>
      {/* Full-width header: title + search */}
      <div className="search-section">
        <h1 className="hero-title">{t('researchList.heroTitlePrefix')} <span>{t('researchList.heroTitleHighlight')}</span></h1>
        <p className="hero-sub">{t('researchList.heroSub')}</p>
        <SearchBar onSubmit={handleSearch} loading={submitting} />

        {error && <div className="error-msg">⚠️ {error}</div>}

        {notice && (
          <div className={`notice-msg notice-${notice.type}`}>
            {notice.type === 'cache' && '💾 '}
            {notice.type === 'stale' && '🔄 '}
            {notice.type === 'info'  && 'ℹ️ '}
            {notice.message}
          </div>
        )}
      </div>

      {runningJobs.length > 0 && (
        <div className="running-banner">
          {runningJobs.map(r => (
            <div key={r._id} className="running-item">
              <span className="spinner-sm" />
              {t('researchList.researchingPrefix')} <strong>{r.name}</strong>...
            </div>
          ))}
        </div>
      )}

      <MarketAlertBanner alerts={marketAlerts} collapsible kinds={['daily']} />

      {/* Below the header: left sidebar (system) | main list (user) */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24, ...(isMobile ? { flexDirection: 'column' } : {}) }}>
        {!isMobile && (
          <aside style={{ width: 280, flexShrink: 0, position: 'sticky', top: 84 }}>
            <DiscoveredPanel />
          </aside>
        )}

        <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
          {!ready ? (
            <div className="empty-state"><div className="empty-sub">{t('researchList.loadingList')}</div></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <div className="empty-title">{t('researchList.emptyTitle')}</div>
              <div className="empty-sub">{t('researchList.emptyDesc')}</div>
            </div>
          ) : (
            <>
              <div className="cards-grid">
                {items.map(doc => (
                  <ResearchCard key={doc._id} doc={doc} onClick={(slug) => navigate(`/research/${slug}`)} onReResearch={handleReResearch} />
                ))}
              </div>
              {items.length < total && <div ref={sentinelRef} style={{ height: 1 }} />}
              {loading && (
                <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>
                  {t('researchList.loadingMore')}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile: discovered feed below the user's list */}
      {isMobile && (
        <div style={{ marginTop: 28, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <DiscoveredPanel />
        </div>
      )}
    </>
  );
}
